import { Calculation } from '@servicetitan/financing-api-client';
import { formatCurrency } from '@servicetitan/financing-utils-shared';

export type AmountRangeStatus = 'loading' | 'in-range' | 'never-in-range' | 'out-of-range';

export interface AmountUnavailableMessage {
    headline: string;
}

export interface AmountRangeSearchInput {
    provider?: string | null;
    tenantId?: string | null;
    customerId?: string | null;
    buId?: string | null;
    state?: string | null;
    customPlanIds?: string[] | null;
    financedAmount?: number | null;
}

const getContextValues = (input: AmountRangeSearchInput) => ({
    provider: input.provider ?? '',
    tenantId: input.tenantId ?? '',
    customerId: input.customerId ?? '',
    buId: input.buId ?? '',
    state: input.state ?? '',
    customPlanIds: [...(input.customPlanIds ?? [])].sort(),
});

export const buildAmountRangeContextKey = (input: AmountRangeSearchInput): string =>
    JSON.stringify(getContextValues(input));

export const buildAmountRangeSearchKey = (input: AmountRangeSearchInput): string =>
    JSON.stringify({
        ...getContextValues(input),
        financedAmount: input.financedAmount ?? null,
    });

export const sortEligibleCalculations = (calculations: Calculation[] = []): Calculation[] =>
    calculations
        .filter(calculation => calculation.eligibleForApplication)
        .sort((first, second) => {
            const firstPayment = Math.min(
                first.defaultMonthlyPayment,
                first.suggestedMonthlyPayment && first.suggestedMonthlyPayment > 0
                    ? first.suggestedMonthlyPayment
                    : first.defaultMonthlyPayment,
            );
            const secondPayment = Math.min(
                second.defaultMonthlyPayment,
                second.suggestedMonthlyPayment && second.suggestedMonthlyPayment > 0
                    ? second.suggestedMonthlyPayment
                    : second.defaultMonthlyPayment,
            );

            return firstPayment - secondPayment || first.defaultRate - second.defaultRate;
        });

export const getPositiveMinimumLoanAmount = (
    calculations: Calculation[] = [],
): number | undefined => {
    const minimums = calculations
        .map(calculation => calculation.minLoanAmount)
        .filter(
            (amount): amount is number => amount != null && Number.isFinite(amount) && amount > 0,
        );

    return minimums.length ? Math.min(...minimums) : undefined;
};

const isPositiveFiniteNumber = (amount?: number): amount is number =>
    amount != null && Number.isFinite(amount) && amount > 0;

const fallbackMessage: AmountUnavailableMessage = {
    headline: 'No financing plans are available for this amount.',
};

export const buildAmountUnavailableMessage = ({
    financedAmount,
    minAmount,
    maxAmount,
    calculations = [],
}: {
    financedAmount: number;
    minAmount?: number;
    maxAmount?: number;
    calculations?: Calculation[];
}): AmountUnavailableMessage => {
    const hasMinimum = isPositiveFiniteNumber(minAmount);
    const hasMaximum = isPositiveFiniteNumber(maxAmount);
    const hasConflictingProviderBounds = hasMinimum && hasMaximum && minAmount > maxAmount;

    if (!isPositiveFiniteNumber(financedAmount) || hasConflictingProviderBounds) {
        return fallbackMessage;
    }

    if (hasMinimum && financedAmount < minAmount) {
        return {
            headline: `Desired financed amount must be at least ${formatCurrency(minAmount)}.`,
        };
    }

    if (hasMaximum && financedAmount > maxAmount) {
        return {
            headline: `Desired financed amount must be at most ${formatCurrency(maxAmount)}.`,
        };
    }

    const hasOnlyIneligibleCalculations =
        calculations.length > 0 &&
        calculations.every(calculation => calculation.eligibleForApplication === false);
    const hasUsableMinimumForEveryCalculation = calculations.every(calculation =>
        isPositiveFiniteNumber(calculation.minLoanAmount),
    );

    if (!hasOnlyIneligibleCalculations || !hasUsableMinimumForEveryCalculation || hasMaximum) {
        return fallbackMessage;
    }

    const minimum = getPositiveMinimumLoanAmount(calculations)!;
    const maximumMinimum = Math.max(...calculations.map(calculation => calculation.minLoanAmount));
    if (financedAmount < minimum) {
        return {
            headline: `Desired financed amount must be at least ${formatCurrency(minimum)}.`,
        };
    }

    if (financedAmount >= maximumMinimum) {
        return { headline: 'Desired financed amount is too large; try lowering it.' };
    }

    return fallbackMessage;
};
